import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../AuthContext";
import { LogOut, BarChart3, ShoppingBag, Layers, FlaskConical, Zap, Menu, X, Package } from "lucide-react";

const linkCls =
  "flex items-center gap-3 px-4 py-3 rounded-sm overline text-ink-soft hover:bg-sage/30 hover:text-ink transition-colors";
const activeCls = "bg-sage/40 !text-matcha-dark";

function NavItems({ onNavigate }) {
  return (
    <>
      <NavLink to="/management" end data-testid="mgmt-nav-dashboard"
        onClick={onNavigate}
        className={({ isActive }) => `${linkCls} ${isActive ? activeCls : ""}`}>
        <BarChart3 size={16} /> Dashboard
      </NavLink>
      <NavLink to="/management/sales" data-testid="mgmt-nav-sales"
        onClick={onNavigate}
        className={({ isActive }) => `${linkCls} ${isActive ? activeCls : ""}`}>
        <ShoppingBag size={16} /> Sales & Orders
      </NavLink>
      <NavLink to="/management/catalog" data-testid="mgmt-nav-catalog"
        onClick={onNavigate}
        className={({ isActive }) => `${linkCls} ${isActive ? activeCls : ""}`}>
        <Layers size={16} /> Products & Inventory
      </NavLink>
      <NavLink to="/management/daily-powder" data-testid="mgmt-nav-daily-powder"
        onClick={onNavigate}
        className={({ isActive }) => `${linkCls} ${isActive ? activeCls : ""}`}>
        <FlaskConical size={16} /> Daily Powder
      </NavLink>
      <NavLink to="/management/quick-usage" data-testid="mgmt-nav-quick-usage"
        onClick={onNavigate}
        className={({ isActive }) => `${linkCls} ${isActive ? activeCls : ""}`}>
        <Zap size={16} /> Quick Usage
      </NavLink>
      <NavLink to="/management/inventory" data-testid="mgmt-nav-inventory"
        onClick={onNavigate}
        className={({ isActive }) => `${linkCls} ${isActive ? activeCls : ""}`}>
        <Package size={16} /> Inventory (Multi-Outlet)
      </NavLink>
    </>
  );
}

export default function MgmtLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const onLogout = () => {
    logout();
    navigate("/staff/login");
  };

  return (
    <div className="min-h-screen bg-cream text-ink flex">
      {/* Sidebar — desktop only */}
      <aside className="hidden md:flex w-64 shrink-0 bg-bone border-r border-ink/10 min-h-screen sticky top-0 flex-col">
        <div className="p-6 border-b border-ink/10">
          <Link to="/management" className="block">
            <span className="font-display block text-ink" style={{ letterSpacing: "0.16em", fontSize: "0.95rem" }}>
              MATCHA<span className="text-matcha-dark">·</span>STORY
            </span>
            <p className="overline text-matcha-dark mt-2 text-[0.62rem]">Operations & BI</p>
          </Link>
        </div>
        <nav className="p-4 space-y-1 flex-1">
          <NavItems />
        </nav>
        <div className="p-4 border-t border-ink/10 space-y-2">
          <Link to="/staff/admin" className={linkCls + " !py-2"}>← Staff Portal</Link>
          <Link to="/" className={linkCls + " !py-2"}>← Site</Link>
        </div>
      </aside>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <>
          <div
            data-testid="mgmt-drawer-scrim"
            className="fixed inset-0 bg-ink/50 z-40 md:hidden"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="fixed left-0 top-0 bottom-0 w-72 max-w-[80vw] bg-bone border-r border-ink/10 z-50 md:hidden flex flex-col shadow-xl animate-in slide-in-from-left duration-200">
            <div className="p-5 border-b border-ink/10 flex items-center justify-between">
              <Link to="/management" onClick={() => setDrawerOpen(false)}>
                <span className="font-display block text-ink" style={{ letterSpacing: "0.14em", fontSize: "0.9rem" }}>
                  MATCHA<span className="text-matcha-dark">·</span>STORY
                </span>
                <p className="overline text-matcha-dark mt-1 text-[0.6rem]">Operations & BI</p>
              </Link>
              <button
                data-testid="mgmt-drawer-close"
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-full hover:bg-ink/5"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
              <NavItems onNavigate={() => setDrawerOpen(false)} />
            </nav>
            <div className="p-4 border-t border-ink/10 space-y-1">
              <Link to="/staff/admin" onClick={() => setDrawerOpen(false)} className={linkCls + " !py-2"}>← Staff Portal</Link>
              <Link to="/" onClick={() => setDrawerOpen(false)} className={linkCls + " !py-2"}>← Site</Link>
            </div>
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="border-b border-ink/10 bg-bone/80 backdrop-blur-xl sticky top-0 z-30">
          <div className="px-4 md:px-10 py-3 md:py-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                data-testid="mgmt-drawer-open"
                onClick={() => setDrawerOpen(true)}
                className="md:hidden p-2 -ml-2 rounded-full hover:bg-ink/5 shrink-0"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
              <div className="min-w-0">
                {subtitle && <p className="overline text-matcha-dark text-[0.6rem] md:text-xs truncate">{subtitle}</p>}
                {title && (
                  <h1
                    className="font-display text-ink truncate"
                    style={{ fontSize: "clamp(1.05rem, 2.4vw, 2rem)", letterSpacing: "0.04em" }}
                  >
                    {title}
                  </h1>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right hidden sm:block">
                <p className="text-sm text-ink">{user?.preferred_name || user?.full_name}</p>
                <p className="overline text-ink-soft">{user?.role}</p>
              </div>
              <button
                data-testid="mgmt-logout-btn"
                onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-full px-3 md:px-4 py-2 border border-ink/30 hover:bg-ink hover:text-bone transition-all duration-300 text-[10px] md:text-xs uppercase tracking-[0.14em] md:tracking-[0.18em]"
              >
                <LogOut size={14} />
                <span className="hidden xs:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>
        <main className="px-4 md:px-10 py-6 md:py-12">{children}</main>
      </div>
    </div>
  );
}
